/**
 * ActionableCheckCard — Iter-38 Stage 2.
 *
 * A single check card. Enforces the consistent rhythm across every issue:
 *
 *   Status → Evidence → How to Fix → Difficulty → Developer Details
 *
 * Rules:
 *   • Green (passing) checks stay COMPACT — same visual density as before.
 *   • Amber / red checks become expandable. Collapsed shows the short
 *     status + severity + difficulty chips. Expanded reveals evidence,
 *     how-to-fix, and the existing developer-details block.
 *   • Older stored reports (no severity / no evidence) fall back gracefully:
 *     they render in the compact style with legacy `items[]` where present,
 *     or a calm "detailed locations were not recorded" message when the
 *     check clearly IS a failure but no per-item data was captured.
 *   • Never invents data. If we don't have it, we say so calmly.
 */
import { useState } from "react";
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import DeveloperDetails from "./DeveloperDetails";
import EvidenceTable from "./EvidenceTable";
import { ScanDiagnostics } from "./ScanDiagnostics";

const SEVERITY_CHIP_CLS = {
  high:
    "bg-red-50 text-red-700 border border-red-200",
  medium:
    "bg-amber-50 text-amber-800 border border-amber-200",
  recommended:
    "bg-charcoal-50 text-charcoal-700 border border-black/10",
};

// Difficulty is intentionally SECONDARY to severity — subtle outline only,
// no fill. Same size as severity chips so they still align, but the
// muted palette prevents them from competing with the urgency signal.
const DIFFICULTY_CHIP_CLS = {
  easy: "bg-white text-emerald-700 border border-emerald-300",
  moderate: "bg-white text-charcoal-600 border border-charcoal-300",
  developer: "bg-white text-charcoal-700 border border-charcoal-500",
};

// Auto-expanded "How to Fix" panel — replaces the old click-to-expand
// CheckHelp component when rendered inside an already-open ActionableCheckCard.
// Iter-38 Stage 2 refinement: the rhythm inside an expanded issue must be a
// single reading pass (Status → Evidence → How to Fix → Technical Details),
// not a nested click chain.
function HowToFixPanel({ help, checkKey }) {
  if (!help || (!help.what && !(help.how && help.how.length))) return null;
  return (
    <div
      className="mt-3 rounded-lg bg-brand/5 border-l-4 border-brand pl-4 pr-3.5 py-3 space-y-2.5"
      data-testid={`check-help-${checkKey}`}
    >
      <p
        className="text-[11px] font-mono uppercase tracking-[0.14em] text-brand"
        data-testid={`check-help-header-${checkKey}`}
      >
        How to Fix
      </p>
      {help.what && (
        <p className="text-[12px] text-charcoal-700 leading-relaxed">
          <span className="font-medium text-charcoal-800">What this means: </span>
          {help.what}
        </p>
      )}
      {help.how && help.how.length > 0 && (
        <ol className="space-y-1.5 pl-4 list-decimal marker:text-brand/60">
          {help.how.map((step, i) => (
            <li key={i} className="text-[12px] text-charcoal-800 leading-relaxed">
              {step}
            </li>
          ))}
        </ol>
      )}
      {help.link && (
        <a
          href={help.link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] font-medium text-brand hover:underline"
          data-testid={`check-help-link-${checkKey}`}
        >
          Learn more <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === "green")
    return <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" strokeWidth={2} />;
  if (status === "amber")
    return <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" strokeWidth={2} />;
  if (status === "red")
    return <AlertCircle className="w-4 h-4 text-red-500 shrink-0" strokeWidth={2} />;
  return <Info className="w-4 h-4 text-charcoal-400 shrink-0" strokeWidth={2} />;
}

function Chip({ text, className, testId }) {
  if (!text) return null;
  return (
    <span
      className={`inline-block text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded ${className}`}
      data-testid={testId}
    >
      {text}
    </span>
  );
}

// "Missing locations" heuristic: this check clearly represents a failure
// with a count in its detail (e.g. "3 links broken") but no evidence[]
// and no legacy items[]. Older stored reports fall into this bucket.
// We only surface the fallback for check keys that we'd EXPECT to carry
// per-item data — content-based checks like SSL, SEO, mission_statement
// deliver their story via `detail` itself and shouldn't be flagged.
const EXPECTS_ITEMS = new Set([
  "links", "broken_links", "pages_broken", "a11y_alt",
  "images", "pdfs", "social", "donation_coverage",
]);
function hasMissingLocations(c) {
  if (c.status === "green") return false;
  if (!EXPECTS_ITEMS.has(c.key)) return false;
  const hasEvidence = Array.isArray(c.evidence) && c.evidence.length > 0;
  const hasItems = Array.isArray(c.items) && c.items.length > 0;
  if (hasEvidence || hasItems) return false;
  const detail = c.detail || "";
  return /\d+/.test(detail); // e.g. "2 pages", "3 links broken"
}

export default function ActionableCheckCard({ check: c, Icon, user, anchorId }) {
  // Green / info checks stay compact — no expander, keep the old visual.
  const isIssue = c.status === "amber" || c.status === "red";
  const hasHelp = !!c.how_to_fix;
  const hasEvidence = Array.isArray(c.evidence) && c.evidence.length > 0;
  const hasLegacyItems = !hasEvidence && Array.isArray(c.items) && c.items.length > 0;
  const missingLocations = hasMissingLocations(c);

  const [open, setOpen] = useState(false);
  // Only *issue* cards are expandable. Passed cards render inline.
  const expandable = isIssue && (hasEvidence || hasLegacyItems || hasHelp || missingLocations || c.developer_detail);

  if (!expandable) {
    return (
      <div
        id={anchorId}
        className="min-w-0 flex items-start gap-3 py-2.5 px-3.5 rounded-xl border border-black/5 bg-charcoal-50/70 scroll-mt-24"
        data-testid={`check-card-${c.key}`}
      >
        <Icon className="w-4 h-4 text-charcoal-400 mt-0.5 shrink-0" strokeWidth={1.75} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-charcoal-800 leading-tight">{c.label}</p>
          <p className="text-[11px] font-mono text-charcoal-400 mt-0.5 truncate">{c.detail}</p>
        </div>
        <StatusIcon status={c.status} />
      </div>
    );
  }

  return (
    <div
      id={anchorId}
      className="min-w-0 sm:col-span-2 rounded-xl border border-black/5 bg-white scroll-mt-24"
      data-testid={`check-card-${c.key}`}
    >
      {/* Collapsed header row — clickable to expand. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-start gap-3 py-2.5 px-3.5 text-left hover:bg-charcoal-50/40 transition-colors rounded-xl"
        data-testid={`check-toggle-${c.key}`}
      >
        <Icon className="w-4 h-4 text-charcoal-400 mt-0.5 shrink-0" strokeWidth={1.75} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm text-charcoal-800 leading-tight">{c.label}</p>
            <Chip
              text={c.severity_label}
              className={SEVERITY_CHIP_CLS[c.severity] || SEVERITY_CHIP_CLS.recommended}
              testId={`chip-severity-${c.key}`}
            />
            <Chip
              text={c.fix_difficulty_label}
              className={DIFFICULTY_CHIP_CLS[c.fix_difficulty] || DIFFICULTY_CHIP_CLS.moderate}
              testId={`chip-difficulty-${c.key}`}
            />
          </div>
          <p className="text-[11px] font-mono text-charcoal-400 mt-1">{c.detail}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0 mt-0.5">
          <StatusIcon status={c.status} />
          {open ? (
            <ChevronDown className="w-4 h-4 text-charcoal-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-charcoal-400" />
          )}
        </div>
      </button>

      {open && (
        <div className="px-3.5 pb-3.5 pl-10" data-testid={`check-body-${c.key}`}>
          {/* --- Evidence (Where it happened) ---------------------------- */}
          {hasEvidence && <EvidenceTable check={c} />}

          {/* Legacy items fallback for old reports */}
          {hasLegacyItems && !hasEvidence && (
            <div className="mt-3">
              <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-charcoal-500 mb-1.5">
                Where it happened
              </p>
              <ul className="space-y-1 rounded-lg bg-charcoal-50/70 border border-black/5 p-3">
                {c.items.slice(0, 10).map((it, i) => (
                  <li key={i} className="flex items-start gap-2 text-[12px] break-all">
                    <span className="mt-1 shrink-0 w-1 h-1 rounded-full bg-charcoal-400" />
                    <a
                      href={typeof it === "string" ? it : it.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-charcoal-700 hover:text-brand hover:underline font-mono text-[11px]"
                    >
                      {typeof it === "string" ? it : it.url}
                    </a>
                    {typeof it !== "string" && it.reason && (
                      <span className="text-[10px] font-mono uppercase tracking-wider text-charcoal-500">
                        {it.reason}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Calm fallback when locations weren't recorded on this audit */}
          {missingLocations && (
            <div
              className="mt-3 rounded-lg bg-charcoal-50/70 border border-black/5 px-3 py-2.5"
              data-testid={`check-no-locations-${c.key}`}
            >
              <p className="text-[12px] text-charcoal-600 leading-relaxed">
                Mission Watch found this issue during the scan, but couldn't
                capture the exact locations this time. Your next scan will
                surface the specific pages or links to fix.
              </p>
            </div>
          )}

          {/* --- How to Fix (customer-facing guidance, auto-expanded) --- */}
          {hasHelp && <HowToFixPanel help={c.how_to_fix} checkKey={c.key} />}

          {/* --- Technical details (existing collapsible block; renamed
              from "Developer details" per Iter-38 Stage 2 refinement — the
              label is nonprofit-friendly, the content is unchanged). */}
          <DeveloperDetails
            text={c.developer_detail}
            testIdSuffix={c.key}
          />
          {user?.is_owner && c.diagnostic && (
            <ScanDiagnostics diagnostic={c.diagnostic} />
          )}
        </div>
      )}
    </div>
  );
}
